// <copyright file="quote-item-grouping.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

/**
 * Quote item grouping document.
 *
 * Pure data structure + operations for grouping (batching) quote items on the quote items view.
 * The document is serialized to JSON and stored as the quote-level blob asset "itemGrouping".
 *
 * Rules:
 *  - The document is the ABSOLUTE source of truth for presentation and ordering, and it holds EVERY live
 *    item - active and excluded-from-pricing alike. `quotePresentation.itemDisplayOrder` is nothing more
 *    than this document FLATTENED: the same order with the group boundaries dropped. That is what lets
 *    the legacy consumers (datapump, item position numbering, server-side item tasks) keep seeing exactly
 *    what the screen shows.
 *  - EVERY CONTAINER ORDERS THE SAME WAY: its user-sortable members in their authored order, then its
 *    charge lines (Shipping, SSI, DealerPriceAdjustment) in a fixed rank - SSI, then shipping, then
 *    dealer price adjustment. The trunk is simply the outermost container, so one rule covers it too:
 *    loose items and groups in authored order, then the quote-level charges last. With no groups at all
 *    the trunk behaves as a single implicit group (without one being created) - and that is precisely the
 *    historic flat presentation, so an ungrouped quote is unchanged BY CONSTRUCTION rather than by a
 *    fallback path.
 *  - A charge is therefore never a special case of ORDERING, only of MOVEMENT: the user cannot position
 *    one. Enforced by normalizeGroupingDocument on every reconcile and every save, so that no reader has
 *    to reconstruct it - three of them used to, each from a link map that lags the document by one save.
 *  - Single-level nesting: the root ("trunk") holds item nodes and group nodes; group nodes hold
 *    item nodes only. The schema is a tree so deeper nesting can be introduced later without a
 *    format change.
 *  - Batching is one-way: for as long as ANY batch exists a PRIMARY batch exists, and no user-SORTABLE item
 *    may sit loose at the trunk - normalize absorbs any that lands there. Anything later added to the quote
 *    therefore defaults into the primary, which needs no special handling: it lands loose and is absorbed.
 *  - A primary holding NOTHING is not rendered, on any surface (see isRenderedGroup). Carve every item out
 *    of "All Items" into batches of their own and the empty catch-all stops being shown rather than sitting
 *    there as a puzzle; add an item and it reappears, because the item defaults back into it. The node is
 *    never deleted - it stays in the document, so the rule is purely about display.
 *  - Charges are untouched by absorption, because WHICH batch a charge belongs to is not ours to decide.
 *    The primary is a batch like any other as far as the supplier is concerned: its V6 may return a charge
 *    per batch, or may put every charge at the bottom of the quote outside all of them, and both are
 *    legitimate. So an unbatched charge is left unbatched and rendered at the trunk's end - the client
 *    presents whatever the supplier decided and never corrects it into or out of a batch. Wherever a charge
 *    lands it sits at the BOTTOM of that container and cannot be moved, sorted or interleaved.
 *  - At most ONE charge of a given type per container: a batch may carry its own shipping, never two, and
 *    the same on the trunk. Enforced where charges are produced rather than here - the supplier's reply is
 *    rejected outright on a duplicate (TaskGetSSI) and SSI identity is keyed (content type, batch) on both
 *    sides (v6-ssi-processor). This document depends on it: two charges of one type in one container would
 *    share a sink rank, leaving no meaningful order between them.
 *  - On a pure order divergence between the document and itemDisplayOrder (same id set, different
 *    sequence) the document wins: every grouping-aware save rewrites itemDisplayOrder.
 *
 * All operations are pure: they clone the input document and return a new one.
 */

import { clone } from '../../../webmodule-common/other/clone.js';
import { localRFC3339DateToServer } from '../../../webmodule-common/other/datetime-converter.js';
import { newGuid } from '../../../webmodule-common/other/api/guid.js';

export const quoteItemGroupingVersion = 1;
export const quoteItemGroupingAssetKey = 'itemGrouping';

export interface QuoteItemGroupAttributes {
  excludeFromPricing: boolean;

  // --- MRD delivery-batch metadata (Task 230006) ---------------------------------------------------
  // A group is a V6 delivery "batch" when it contains order-bound items. These fields are the client
  // edit buffer for the batch; the DB `QuoteItemBatch` table is the source of truth (reconciled on
  // load, persisted on save). The group node `id` is the batch's **CommonId** — the presentation
  // identity, which survives a quote copy — NOT its row PK, which is what crosses to V6 and the
  // purchase order. All optional so existing (pre-MRD) grouping documents parse unchanged.

  /** Dealer-requested delivery date (ISO 8601). The "required date" of a multi-required-date order. */
  deliveryDate?: string | null;
  /** Dealer customer reference for this delivery. */
  customerRef?: string | null;
  /** Dealer comment for this delivery. */
  comment?: string | null;
  /** Dealer-authored starting status. V6 owns the status lifecycle post-PO (synced read-only). */
  status?: string | null;

  // forward-extensible: unknown keys must survive a parse/serialize round-trip
  [key: string]: unknown;
}

export interface GroupingItemNode {
  type: 'item';
  /** QuoteItem.commonId */
  id: string;
}

export interface GroupingGroupNode {
  type: 'group';
  id: string;
  name: string;
  attributes: QuoteItemGroupAttributes;
  /** today: item nodes only (enforced by operations, not by the type) */
  children: GroupingNode[];
}

export type GroupingNode = GroupingItemNode | GroupingGroupNode;

export interface QuoteItemGroupingDocument {
  version: number;
  /** the trunk: array order = display order */
  root: GroupingNode[];
}

/**
 * Which items the document should hold, and which of them the user may not position.
 *
 * Every operation that writes the document takes this, so the placement rules are applied where the
 * document is built rather than re-derived by each of its readers. Three readers used to reconstruct
 * "charges sink to the bottom of their section" independently - the presentation order, the table's row
 * builder and the numbering - each from the item→batch link map, and they could disagree with each other
 * mid-regroup because the link map lags the document by one save.
 */
export interface ItemPlacementRules {
  /** Every item the document should contain: the live items (active + excluded-from-pricing). */
  items: ReadonlySet<string>;
  /**
   * Sink rank of a supplier charge line - lower sinks first - or null when the item is user-sortable.
   * The ranks reproduce the historic presentation order: SSI, then shipping, then price adjustment.
   */
  forcedRank(commonId: string): number | null;
}

/**
 * Placement rules for a document with no charge lines in it. For callers and tests that only deal in
 * user-sortable items; a document built with these is still normalized, there is simply nothing to sink.
 */
export function sortableItemsOnly(items: ReadonlySet<string>): ItemPlacementRules {
  return { items, forcedRank: () => null };
}

/**
 * A batch delivery date as the API expects it: a SERVER date string (local start-of-day expressed as UTC —
 * the app-wide day-date shape, see localDateToServer), or null.
 *
 * Two normalizations, and this is the single chokepoint for both — every boundary that sends the value
 * (the quote save's batch inputs and the SSI quote contract) goes through here.
 *
 * 1. Blank -> null. An untouched date input yields `''`, and `""` is not something System.Text.Json can
 *    convert to a `DateTimeOffset?` — it rejects the whole request, so creating a group with no delivery
 *    date failed the entire quote save with a 400. This also cleans up any blob that already stored `''`.
 *
 * 2. A bare `yyyy-MM-dd` -> the server shape. The dialog binds the field as FieldType.date so the tracker
 *    already converts it, but the attribute can still hold a bare date from a blob written before that
 *    binding was corrected. Sending one means the SERVER decides the offset (DateTimeOffset.Parse uses the
 *    server's local zone), so the same calendar day would be stored differently depending on where it was
 *    parsed, and would come back a day out for the dealer who typed it. Pinning the conversion to the
 *    author's browser zone is what makes the round trip lossless.
 */
export function normalizeBatchDate(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? localRFC3339DateToServer(trimmed) : trimmed;
}

/**
 * Attribute marking the PRIMARY batch (Task 230006) — the catch-all every item belongs to unless it is in
 * a batch of its own. Marked explicitly rather than inferred from trunk position, because the user can
 * reorder batches freely and "the first group" would stop being the primary the moment they did.
 *
 * Batching is one-way: the primary is created the first time any group is made, and from then on no
 * user-SORTABLE item sits loose at the trunk. The quote-level charges still do - they belong to the quote,
 * not to the primary - so "fully batched" describes the items the dealer places, not every line. The
 * primary is a real V6 batch in every other respect — renameable, with its own delivery date / customer
 * reference / comment.
 */
export const primaryBatchAttributeKey = 'isPrimaryBatch';

export function isPrimaryGroup(node: GroupingNode): boolean {
  return isGroupNode(node) && node.attributes[primaryBatchAttributeKey] === true;
}

export function findPrimaryGroup(doc: QuoteItemGroupingDocument): GroupingGroupNode | undefined {
  return doc.root.filter(isGroupNode).find(g => g.attributes[primaryBatchAttributeKey] === true);
}

/**
 * Should this group be rendered at all? Applies to every surface - the items table, the quote report, the
 * purchase order and its report.
 *
 * The PRIMARY batch is suppressed while it holds NOTHING. Carve every item out of "All Items" into batches
 * of their own and the catch-all is left empty, where it reads as a puzzle rather than as information. This
 * is display only: the node stays in the document, so adding an item - which defaults back into the primary
 * - brings it straight back into view.
 *
 * "Nothing" means no child rows at all, charges included. A suppressed header cannot hold a visible row, so
 * a primary carrying only a quote-level charge is still rendered.
 *
 * Any OTHER empty group is rendered as normal: the dealer made it deliberately and needs a target to drag
 * items into.
 */
export function isRenderedGroup(node: GroupingGroupNode): boolean {
  return !isPrimaryGroup(node) || node.children.some(isItemNode);
}

export function isGroupNode(node: GroupingNode): node is GroupingGroupNode {
  return node.type === 'group';
}

export function isItemNode(node: GroupingNode): node is GroupingItemNode {
  return node.type === 'item';
}

export function createEmptyGroupingDocument(): QuoteItemGroupingDocument {
  return { version: quoteItemGroupingVersion, root: [] };
}

export function serializeGroupingDocument(doc: QuoteItemGroupingDocument): string {
  return JSON.stringify(doc);
}

/**
 * Parse and structurally validate a grouping document. Returns null on any failure so the caller
 * can rebuild from the legacy itemDisplayOrder. Unknown attribute keys are preserved.
 */
export function parseGroupingDocument(json: string | null | undefined): QuoteItemGroupingDocument | null {
  if (!json) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== 'object') return null;
  const docCandidate = raw as { version?: unknown; root?: unknown };
  if (typeof docCandidate.version !== 'number' || !Array.isArray(docCandidate.root)) return null;

  const parseNode = (node: unknown, allowGroup: boolean): GroupingNode | null => {
    if (!node || typeof node !== 'object') return null;
    const n = node as { type?: unknown; id?: unknown; name?: unknown; attributes?: unknown; children?: unknown };
    if (n.type === 'item') {
      if (typeof n.id !== 'string' || !n.id) return null;
      return { type: 'item', id: n.id };
    }
    if (n.type === 'group' && allowGroup) {
      if (typeof n.id !== 'string' || !n.id) return null;
      if (typeof n.name !== 'string') return null;
      const attributes: QuoteItemGroupAttributes =
        n.attributes && typeof n.attributes === 'object'
          ? { excludeFromPricing: false, ...(n.attributes as object) }
          : { excludeFromPricing: false };
      attributes.excludeFromPricing = attributes.excludeFromPricing === true;
      const children: GroupingNode[] = [];
      if (n.children !== undefined && !Array.isArray(n.children)) return null;
      for (const child of (n.children as unknown[]) ?? []) {
        // groups may only contain item nodes (single-level nesting)
        const parsed = parseNode(child, false);
        if (parsed) children.push(parsed);
      }
      return { type: 'group', id: n.id, name: n.name, attributes, children };
    }
    return null;
  };

  const root: GroupingNode[] = [];
  for (const node of docCandidate.root) {
    const parsed = parseNode(node, true);
    if (parsed) root.push(parsed);
  }
  return { version: docCandidate.version, root };
}

/**
 * Build a fresh document from the legacy itemDisplayOrder: every item becomes a trunk item node, in
 * order. Used as the migration fallback when the asset is missing or invalid.
 *
 * itemDisplayOrder already holds every item with the charges at the end (that is what
 * QuoteContainerManager's presentation sort has always produced), so this is close to a straight copy;
 * normalizing anyway is what guarantees it, whatever state the legacy order was left in.
 */
export function buildGroupingFromDisplayOrder(
  itemDisplayOrder: readonly string[],
  rules: ItemPlacementRules
): QuoteItemGroupingDocument {
  const doc = createEmptyGroupingDocument();
  const seen = new Set<string>();
  for (const id of itemDisplayOrder) {
    if (!rules.items.has(id) || seen.has(id)) continue;
    seen.add(id);
    doc.root.push({ type: 'item', id });
  }
  // defensive: items missing from the display order still need a home
  for (const id of rules.items) {
    if (!seen.has(id)) doc.root.push({ type: 'item', id });
  }
  return normalizeGroupingDocument(doc, rules).doc;
}

/** Ordered commonIds of every item in the document (trunk + groups, display order). */
export function flattenGroupingToDisplayOrder(doc: QuoteItemGroupingDocument): string[] {
  const result: string[] = [];
  for (const node of doc.root) {
    if (isItemNode(node)) result.push(node.id);
    else for (const child of node.children) if (isItemNode(child)) result.push(child.id);
  }
  return result;
}

export function findGroup(doc: QuoteItemGroupingDocument, groupId: string): GroupingGroupNode | undefined {
  return doc.root.filter(isGroupNode).find(g => g.id === groupId);
}

export function getGroups(doc: QuoteItemGroupingDocument): GroupingGroupNode[] {
  return doc.root.filter(isGroupNode);
}

/** The id of the group containing the item, or null when the item is at trunk level (or absent). */
export function getItemGroupId(doc: QuoteItemGroupingDocument, commonId: string): string | null {
  for (const node of doc.root) {
    if (isGroupNode(node) && node.children.some(c => isItemNode(c) && c.id === commonId)) return node.id;
  }
  return null;
}

/** Set of every item id in the document (trunk + groups), regardless of grouping. */
export function allItemIds(doc: QuoteItemGroupingDocument): Set<string> {
  return new Set(flattenGroupingToDisplayOrder(doc));
}

/**
 * Map of every grouped item's commonId → its group (batch) id. Loose (ungrouped) items are omitted.
 * Includes a batch's own charge lines, which sit inside it: this is what the quote save turns into the
 * item→batch link rows, and a charge that lost its link would lose its batch identity on the next load.
 */
export function batchMembershipMap(doc: QuoteItemGroupingDocument): Map<string, string> {
  const map = new Map<string, string>();
  for (const node of doc.root) {
    if (!isGroupNode(node)) continue;
    for (const child of node.children) if (isItemNode(child)) map.set(child.id, node.id);
  }
  return map;
}

/**
 * True when any USER-AUTHORED batch membership differs between two grouping documents — i.e. the dealer
 * moved an item into, out of, or between batches, or a batched item was removed. This is the trigger for
 * deferring the per-batch SSI (each batch's charge becomes stale the moment its composition changes).
 * Pure reordering of loose items (no batch touched) returns false.
 *
 * The charge lines are excluded deliberately, even though the document holds them: their membership is
 * an OUTPUT of the SSI run, not an authoring decision. Counting it would make every recompute look like
 * a composition change - Update Price re-runs SSI, SSI re-pins its charges, that reads as a change, and
 * the quote defers itself again the moment it was brought up to date.
 */
export function batchMembershipChanged(
  before: QuoteItemGroupingDocument,
  after: QuoteItemGroupingDocument,
  rules: ItemPlacementRules
): boolean {
  const authored = (doc: QuoteItemGroupingDocument): Map<string, string> => {
    const map = batchMembershipMap(doc);
    for (const id of [...map.keys()]) if (rules.forcedRank(id) !== null) map.delete(id);
    return map;
  };
  const a = authored(before);
  const b = authored(after);
  if (a.size !== b.size) return true;
  for (const [id, groupId] of a) if (b.get(id) !== groupId) return true;
  return false;
}

/**
 * commonIds of every item that belongs to a group flagged `excludeFromPricing`. These are the
 * items whose materialized state should be ExcludeFromPricing; every other user-sortable item should
 * be Active.
 *
 * A charge line inside an excluded batch is skipped: this drives an item-state write, and a charge's
 * state belongs to the SSI run that produced it. Materializing it here would have the quote and the
 * supplier each asserting a different state for the same line.
 */
export function excludedFromPricingCommonIds(doc: QuoteItemGroupingDocument, rules: ItemPlacementRules): Set<string> {
  const result = new Set<string>();
  for (const node of doc.root) {
    if (!isGroupNode(node) || !node.attributes.excludeFromPricing) continue;
    for (const child of node.children) {
      if (isItemNode(child) && rules.forcedRank(child.id) === null) result.add(child.id);
    }
  }
  return result;
}

/** Structural signature - node order plus group membership - for telling whether normalizing moved anything. */
function documentShape(doc: QuoteItemGroupingDocument): string {
  return doc.root
    .map(node =>
      isItemNode(node)
        ? node.id
        : `${node.id}[${node.children
            .filter(isItemNode)
            .map(c => c.id)
            .join(',')}]`
    )
    .join('|');
}

/**
 * Enforce the document's layout. This is where the placement rules live, so every operation that writes
 * the document gets them for free: normalize runs at the end of every reconcile and at the top of every
 * grouping mutation, which makes it the one place they cannot be forgotten.
 *
 * ORDERING — ONE rule, applied to every container, the trunk included: sortable members in their authored
 * order, then the charge lines by rank. So a group reads its items then its own charges, and the trunk
 * reads its loose items and groups then the quote-level charges. Nothing about a charge is special to
 * ordering; the only thing that sets it apart is that the user cannot place it. With no groups present the
 * trunk is the single implicit container and this reduces to the historic flat presentation exactly.
 *
 * MOVEMENT — batching is one-way. Once a PRIMARY batch exists no SORTABLE item may sit loose at the trunk:
 * any that lands there - dragged out of a batch, inserted by reconcile, spliced back when a batch was
 * deleted - is absorbed into the primary, keeping its relative order. reconcileGrouping and deleteGroup
 * therefore need no primary-specific logic of their own.
 *
 * Absorption is a MOVEMENT rule, so charges are outside it - and more than that, batch ownership of a
 * charge is not this layer's decision to make. The primary ("All Items") is just another batch to the
 * supplier: its V6 may compute a charge per batch, or leave every charge at the bottom of the quote outside
 * all of them. Both are valid, and the client's job is to present what came back. Absorbing charges made it
 * assert an answer instead: a quote-level warranty was swept inside "All Items", from where it jumped
 * straight back out the moment Update Price re-ran SSI - two paths disagreeing about one line. Specified in
 * QA plan §6.10.
 */
export function normalizeGroupingDocument(
  doc: QuoteItemGroupingDocument,
  rules: ItemPlacementRules
): {
  doc: QuoteItemGroupingDocument;
  changed: boolean;
} {
  const result = clone(doc);

  const rankOf = (node: GroupingNode): number | null => (isItemNode(node) ? rules.forcedRank(node.id) : null);
  const isCharge = (node: GroupingNode): boolean => rankOf(node) !== null;
  const sinkCharges = (nodes: GroupingNode[]): GroupingNode[] => [
    ...nodes.filter(node => !isCharge(node)),
    ...nodes.filter(isCharge).sort((a, b) => rankOf(a)! - rankOf(b)!)
  ];

  for (const node of result.root) {
    if (isGroupNode(node)) node.children = sinkCharges(node.children);
  }

  const loose = result.root.filter(node => isItemNode(node) && !isCharge(node));
  const groups = result.root.filter(isGroupNode);
  const trunkCharges = sinkCharges(result.root.filter(isCharge));

  const primary = findPrimaryGroup(result);
  if (primary && loose.length > 0) {
    primary.children = sinkCharges([...primary.children, ...loose]);
    result.root = [...groups, ...trunkCharges];
  } else {
    result.root = [...loose, ...groups, ...trunkCharges];
  }

  // Compare shapes rather than node identities: the clone above means nothing is identity-equal, and it
  // is the sequence and the membership that callers act on (a `changed` document gets persisted).
  return documentShape(doc) === documentShape(result) ? { doc, changed: false } : { doc: result, changed: true };
}

/**
 * Reconcile the document against the live quote state. Handles items added/removed server-side
 * (create/copy/delete/price adjustment) and any drift from stale clients.
 *
 * 1. Drop item nodes for ids that are no longer live (deleted, pruned) and duplicates.
 * 2. Insert ids present in itemDisplayOrder but missing from the document, after their nearest
 *    preceding known id; when that predecessor sits inside a group the new item lands in the trunk
 *    directly after that group (items are never auto-inserted into a group).
 * 3. Append any live id known to neither, at the trunk end.
 * 4. Normalize, which is what actually places the result (see normalizeGroupingDocument).
 * Empty groups are kept.
 *
 * A charge line the supplier has just created enters here like any other new item - step 2 or 3 puts it
 * somewhere and normalize sinks it to the end of its container. Where it lands is the trunk unless the
 * item→batch links say otherwise, which reconcileBatchesIntoGrouping applies on a genuine (re)load.
 */
export function reconcileGrouping(
  doc: QuoteItemGroupingDocument,
  rules: ItemPlacementRules,
  itemDisplayOrder: readonly string[]
): { doc: QuoteItemGroupingDocument; changed: boolean } {
  const result = clone(doc);
  let changed = false;

  // step 1: drop unknown/duplicate item nodes
  const seen = new Set<string>();
  const keepItem = (node: GroupingItemNode): boolean => {
    if (!rules.items.has(node.id) || seen.has(node.id)) return false;
    seen.add(node.id);
    return true;
  };
  const beforeCounts = result.root.length;
  result.root = result.root.filter(node => (isItemNode(node) ? keepItem(node) : true));
  if (result.root.length !== beforeCounts) changed = true;
  for (const node of result.root) {
    if (!isGroupNode(node)) continue;
    const before = node.children.length;
    node.children = node.children.filter(child => isItemNode(child) && keepItem(child));
    if (node.children.length !== before) changed = true;
  }

  // step 2: insert ids from itemDisplayOrder that the document does not know yet
  const missing = itemDisplayOrder.filter(id => rules.items.has(id) && !seen.has(id));
  for (const id of missing) {
    const orderIndex = itemDisplayOrder.indexOf(id);
    let insertAt = -1;
    for (let i = orderIndex - 1; i >= 0; i--) {
      const predecessor = itemDisplayOrder[i];
      if (!seen.has(predecessor)) continue;
      insertAt = result.root.findIndex(
        node =>
          (isItemNode(node) && node.id === predecessor) ||
          (isGroupNode(node) && node.children.some(c => isItemNode(c) && c.id === predecessor))
      );
      break;
    }
    result.root.splice(insertAt + 1, 0, { type: 'item', id });
    seen.add(id);
    changed = true;
  }

  // step 3: live ids known to neither the document nor the display order
  for (const id of rules.items) {
    if (seen.has(id)) continue;
    result.root.push({ type: 'item', id });
    seen.add(id);
    changed = true;
  }

  // step 4: place everything - sortable items before groups, charges at the end of their container
  const normalized = normalizeGroupingDocument(result, rules);
  return { doc: normalized.doc, changed: changed || normalized.changed };
}

/**
 * A committed delivery-batch row (the DB `QuoteItemBatch`, dealer-authored fields only). The DB is the
 * source of truth for batch identity + metadata; the grouping blob is presentation.
 */
export interface QuoteItemBatchRow {
  id: string;
  description: string;
  excludeFromPricing: boolean;
  /** Marks the catch-all batch. Persisted, so a group rebuilt from its DB row stays the primary. */
  isPrimary?: boolean;
  deliveryDate?: string | null;
  customerRef?: string | null;
  comment?: string | null;
  status?: string | null;
}

/** Remove the item node with this commonId from wherever it sits (trunk or a group); return it, or null. */
function detachItemNode(doc: QuoteItemGroupingDocument, commonId: string): GroupingItemNode | null {
  const rootIndex = doc.root.findIndex(n => isItemNode(n) && n.id === commonId);
  if (rootIndex >= 0) return doc.root.splice(rootIndex, 1)[0] as GroupingItemNode;
  for (const node of doc.root) {
    if (!isGroupNode(node)) continue;
    const childIndex = node.children.findIndex(c => isItemNode(c) && c.id === commonId);
    if (childIndex >= 0) return node.children.splice(childIndex, 1)[0] as GroupingItemNode;
  }
  return null;
}

/**
 * Self-healing reconcile of the presentation grouping document against the freshly-loaded DB state
 * (Task 230006, §3.3b). Because the batch DB table is the source of truth and the blob is only
 * presentation, on load the two can drift (a failed blob write, an out-of-band change, a stale client);
 * this corrects the blob to match the DB, and returns `changed` so the caller can persist the healed doc.
 *
 * On load (both inputs are server truth, so there are no unsaved client groups to protect):
 *  1. Drop stale batch-groups — a group whose id is not a DB batch (its row was deleted); its items
 *     splice back to the trunk.
 *  2. Refresh surviving groups' name + batch attributes from the DB row (DB wins).
 *  3. Add detached DB batches — a row with no group in the blob — as a new group.
 *  4. Correct item membership to match the link table: move any item sitting under the wrong group, or
 *     loose when it should be grouped, or grouped when the DB has no link for it.
 *  5. Normalize, which places the result.
 *
 * Step 4 is also how a charge reaches its batch. The link table is where the SSI run records which batch
 * (if any) computed a charge, so for those lines it is not merely authoritative, it is the ONLY statement
 * of the supplier's decision - the dealer never places a charge, and this layer never invents a placement.
 * Item add/remove against the live item set is still handled separately by {@link reconcileGrouping}.
 *
 * No-ops when the quote has NO batch rows at all. "DB wins" is only meaningful once the DB has something
 * to say. A quote whose blob has groups but whose batch table is empty has simply not been saved since the
 * batch tables existed; its groups are unresolved, not stale. Dissolving them on sight would be the one
 * outcome that cannot be recovered from — there would be nothing left for the next save to resolve. So the
 * groups are left standing and the first save materializes them into real batch rows (see
 * QuoteContainerManager.buildBatchSaveInputs), after which this heal applies normally forever after.
 *
 * This is a convergence rule, not a migration: grouping shipped before batches, so a handful of pre-batch
 * grouping documents can exist, but there is no separate backfill and none is wanted.
 */
export function reconcileBatchesIntoGrouping(
  doc: QuoteItemGroupingDocument,
  dbBatches: readonly QuoteItemBatchRow[],
  itemBatchMembership: ReadonlyMap<string, string | null>,
  rules: ItemPlacementRules
): { doc: QuoteItemGroupingDocument; changed: boolean } {
  if (dbBatches.length === 0) return { doc, changed: false };

  const result = clone(doc);
  let changed = false;
  const dbById = new Map(dbBatches.map(b => [b.id, b]));

  const attributesFromRow = (b: QuoteItemBatchRow): QuoteItemGroupAttributes => ({
    excludeFromPricing: b.excludeFromPricing,
    deliveryDate: b.deliveryDate ?? null,
    customerRef: b.customerRef ?? null,
    comment: b.comment ?? null,
    status: b.status ?? null,
    [primaryBatchAttributeKey]: b.isPrimary === true
  });

  // 1) drop stale batch-groups (present in the blob, gone from the DB)
  for (let i = result.root.length - 1; i >= 0; i--) {
    const node = result.root[i];
    if (isGroupNode(node) && !dbById.has(node.id)) {
      result.root.splice(i, 1, ...node.children);
      changed = true;
    }
  }

  // 2) refresh metadata of surviving groups from the DB (DB wins)
  const present = new Set<string>();
  for (const node of result.root) {
    if (!isGroupNode(node)) continue;
    const db = dbById.get(node.id);
    if (!db) continue;
    present.add(node.id);
    const before = `${node.name}|${JSON.stringify(node.attributes)}`;
    node.name = db.description;
    node.attributes = { ...node.attributes, ...attributesFromRow(db) };
    if (`${node.name}|${JSON.stringify(node.attributes)}` !== before) changed = true;
  }

  // 3) add detached DB batches (row exists, no group yet)
  for (const db of dbBatches) {
    if (present.has(db.id)) continue;
    result.root.push({
      type: 'group',
      id: db.id,
      name: db.description,
      attributes: attributesFromRow(db),
      children: []
    });
    present.add(db.id);
    changed = true;
  }

  // 4) correct item membership to match the authoritative link table
  const groupById = new Map(result.root.filter(isGroupNode).map(g => [g.id, g]));
  for (const [commonId, targetBatchId] of itemBatchMembership) {
    const target = targetBatchId ?? null;
    if (getItemGroupId(result, commonId) === target) continue;
    const node = detachItemNode(result, commonId);
    if (!node) continue; // item not in the doc (not groupable) — reconcileGrouping owns that
    if (target && groupById.has(target)) groupById.get(target)!.children.push(node);
    else result.root.push(node);
    changed = true;
  }

  // 4b) the other direction: an item the blob shows inside a group that the link table has no row for.
  // The map only lists LINKED items, so the loop above can never visit these — it would silently leave the
  // blob claiming a membership the DB has dropped. Walk the document to find them and return them to the
  // trunk.
  for (const group of result.root.filter(isGroupNode)) {
    for (const child of [...group.children]) {
      if (!isItemNode(child) || itemBatchMembership.get(child.id)) continue;
      const node = detachItemNode(result, child.id);
      if (!node) continue;
      result.root.push(node);
      changed = true;
    }
  }

  const normalized = normalizeGroupingDocument(result, rules);
  return { doc: normalized.doc, changed: changed || normalized.changed };
}

/**
 * Place an item in a group, or back at the trunk when groupId is null, and re-normalize.
 *
 * This is how the SUPPLIER's answer reaches the document. A charge line's batch is decided by the SSI run,
 * not by a dealer edit, and it arrives through the item→batch link map — but the document is what the save
 * derives the links from and what every surface renders. Recording it only in the link map left the document
 * saying "trunk" while the link map said "batch X", and because the save writes the DOCUMENT's answer, the
 * next save deleted the link the SSI run had just established: a per-batch charge dropped out to quote level
 * and stayed there.
 *
 * The item is ADDED if the document does not hold it yet. A charge materialized by SSI is live before the
 * next reconcile runs, and the document is supposed to hold every live item; leaving it out would lose the
 * membership for exactly the same reason. Items that are not live are ignored.
 */
export function assignItemToGroup(
  doc: QuoteItemGroupingDocument,
  commonId: string,
  groupId: string | null,
  rules: ItemPlacementRules
): { doc: QuoteItemGroupingDocument; changed: boolean } {
  if (!rules.items.has(commonId)) return { doc, changed: false };

  const present = allItemIds(doc).has(commonId);
  if (present && getItemGroupId(doc, commonId) === groupId) return { doc, changed: false };

  const result = clone(doc);
  const node = detachItemNode(result, commonId) ?? { type: 'item' as const, id: commonId };
  const target = groupId ? result.root.filter(isGroupNode).find(g => g.id === groupId) : undefined;
  // A group id with no group left (deleted batch) puts the item back at the trunk, same as the load-time heal.
  if (target) target.children.push(node);
  else result.root.push(node);

  return { doc: normalizeGroupingDocument(result, rules).doc, changed: true };
}

/**
 * Per-section item numbering (Task 230006, feature 7). Returns commonId → display number.
 *
 * Convention (decided): each container numbers its own members `1..x` - the trunk has one counter and every
 * group restarts - so a quote with no groups is just flat `1..N`. Numbers are per-section, so the group
 * header (and Group Total row) is what distinguishes a loose "1" from a group's "1"; adding / removing /
 * reordering other groups never renumbers a section's items.
 *
 * EVERY member is numbered, the charge lines included: a charge is a member of its container that happens
 * to be pinned to the end of it, so a batch of two windows plus its own shipping numbers 1, 2, 3 and the
 * header counts 3 items. Numbering off the document is what makes that fall out - the charges are in it,
 * in position, so there is nothing to skip and nothing to append afterwards.
 */
export function computeBatchItemNumbers(doc: QuoteItemGroupingDocument): Map<string, string> {
  const result = new Map<string, string>();

  // The trunk shares one running counter; each group has its own counter that restarts at 1.
  let looseWithin = 0;
  for (const node of doc.root) {
    if (isItemNode(node)) {
      result.set(node.id, String(++looseWithin));
    } else if (isGroupNode(node)) {
      let within = 0;
      for (const child of node.children) {
        if (isItemNode(child)) result.set(child.id, String(++within));
      }
    }
  }
  return result;
}

export function createGroup(
  doc: QuoteItemGroupingDocument,
  name: string,
  attributes?: Partial<QuoteItemGroupAttributes>,
  trunkIndex?: number,
  id?: string
): { doc: QuoteItemGroupingDocument; groupId: string } {
  const result = clone(doc);
  const groupId = id ?? newGuid();
  if (findGroup(result, groupId)) throw new Error(`Group "${groupId}" already exists`);
  const group: GroupingGroupNode = {
    type: 'group',
    id: groupId,
    name,
    attributes: { excludeFromPricing: false, ...attributes },
    children: []
  };
  const index = trunkIndex === undefined ? result.root.length : Math.max(0, Math.min(trunkIndex, result.root.length));
  result.root.splice(index, 0, group);
  return { doc: result, groupId };
}

export function renameGroup(doc: QuoteItemGroupingDocument, groupId: string, name: string): QuoteItemGroupingDocument {
  const result = clone(doc);
  const group = requireGroup(result, groupId);
  group.name = name;
  return result;
}

export function setGroupAttributes(
  doc: QuoteItemGroupingDocument,
  groupId: string,
  attributes: Partial<QuoteItemGroupAttributes>
): QuoteItemGroupingDocument {
  const result = clone(doc);
  const group = requireGroup(result, groupId);
  group.attributes = { ...group.attributes, ...attributes };
  return result;
}

/**
 * Delete a group; its children splice into the trunk at the group's position — and its items are then swept
 * into the primary batch by {@link normalizeGroupingDocument}, so deleting a batch returns them to the
 * catch-all rather than stranding them loose. Any charge the batch owned is spliced out with them but NOT
 * absorbed: it lands at the trunk's end, a quote-level charge again, which is the truthful outcome until
 * the next SSI run decides where it now belongs.
 *
 * The primary itself cannot be deleted: batching is one-way, and removing the catch-all would leave every
 * item it holds with nowhere to go.
 *
 * UNBIND ONLY, decided deliberately (2026-07-30): deleting a batch never deletes the items in it. A batch is a
 * delivery segment, not a container the items belong to - they have their own prices, configurations and
 * support history, and the destructive version is trivially reachable anyway (delete the items, then the
 * batch) while the reverse is not. It also keeps the confirmation honest: "Delete Kitchen?" reads as removing a
 * grouping, and a dialog that quietly took four configured frames with it would be a different, far scarier
 * action wearing the same label. If item-deleting behaviour is ever wanted it belongs on its own menu entry
 * with its own confirmation naming the count - not as a change to what Delete means.
 */
export function deleteGroup(doc: QuoteItemGroupingDocument, groupId: string): QuoteItemGroupingDocument {
  const result = clone(doc);
  const index = result.root.findIndex(node => isGroupNode(node) && node.id === groupId);
  if (index < 0) throw new Error(`Group "${groupId}" not found`);
  const group = result.root[index] as GroupingGroupNode;
  if (group.attributes[primaryBatchAttributeKey] === true) {
    throw new Error(`Group "${groupId}" is the primary batch and cannot be deleted`);
  }

  result.root.splice(index, 1, ...group.children);
  return result;
}

/**
 * Move an item to a position within a container (a group, or the trunk when groupId is null).
 * The index addresses the container's children AFTER the item has been removed from its current
 * position (splice semantics). Trunk indices count all trunk nodes - items, groups and the quote-level
 * charges that close out the trunk.
 *
 * Only user-sortable items should be passed here; the charge lines are not user-placeable (the table's
 * drag handlers refuse them). This function does not enforce that, because it is the caller that knows an
 * item's kind - but an index landing among or beyond the trailing charges is corrected by the next
 * normalize regardless, so a stray move degrades to "appended", never to a broken document.
 */
export function moveItem(
  doc: QuoteItemGroupingDocument,
  commonId: string,
  target: { groupId: string | null; index: number }
): QuoteItemGroupingDocument {
  const result = clone(doc);

  let itemNode: GroupingItemNode | undefined;
  const rootIndex = result.root.findIndex(node => isItemNode(node) && node.id === commonId);
  if (rootIndex >= 0) {
    itemNode = result.root.splice(rootIndex, 1)[0] as GroupingItemNode;
  } else {
    for (const node of result.root) {
      if (!isGroupNode(node)) continue;
      const childIndex = node.children.findIndex(c => isItemNode(c) && c.id === commonId);
      if (childIndex >= 0) {
        itemNode = node.children.splice(childIndex, 1)[0] as GroupingItemNode;
        break;
      }
    }
  }
  if (!itemNode) throw new Error(`Item "${commonId}" not found in grouping document`);

  const container = target.groupId === null ? result.root : requireGroup(result, target.groupId).children;
  const index = Math.max(0, Math.min(target.index, container.length));
  container.splice(index, 0, itemNode);
  return result;
}

/**
 * Move a group to a new trunk position. The index addresses the trunk AFTER the group has been
 * removed from its current position (splice semantics). The group's items move with it.
 */
export function moveGroup(
  doc: QuoteItemGroupingDocument,
  groupId: string,
  trunkIndex: number
): QuoteItemGroupingDocument {
  const result = clone(doc);
  const index = result.root.findIndex(node => isGroupNode(node) && node.id === groupId);
  if (index < 0) throw new Error(`Group "${groupId}" not found`);
  const group = result.root.splice(index, 1)[0];
  const insertAt = Math.max(0, Math.min(trunkIndex, result.root.length));
  result.root.splice(insertAt, 0, group);
  return result;
}

function requireGroup(doc: QuoteItemGroupingDocument, groupId: string): GroupingGroupNode {
  const group = findGroup(doc, groupId);
  if (!group) throw new Error(`Group "${groupId}" not found`);
  return group;
}
